import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';

export class RateLimitManager {
    constructor(db, userId, rateLimits) {
        this.db = db;
        this.userId = userId;
        this.rateLimits = rateLimits;
        this.localCounters = {};
        this.windowSize = 60000; // 1 minute in milliseconds
        this.syncThreshold = 10;
    }

    // Helper to safely get milliseconds from a timestamp
    _getMillis(timestamp) {
        if (timestamp instanceof Timestamp) {
            return timestamp.toMillis();
        } else if (typeof timestamp === 'number') {
            return timestamp;
        } else {
            // Return current time as fallback
            return Date.now();
        }
    }

    async _getServerTimestamp() {
        try {
            const tempRef = doc(this.db, 'users', this.userId, 'counters', 'timestamp');
            await setDoc(tempRef, { timestamp: serverTimestamp() });
            const tempDoc = await getDoc(tempRef);
            const timestamp = tempDoc.data()?.timestamp;

            if (timestamp instanceof Timestamp) {
                return timestamp;
            }
            // Fallback to current time if serverTimestamp isn't available
            return Timestamp.fromDate(new Date());
        } catch (error) {
            console.error('Failed to get server timestamp:', error);
            return Timestamp.fromDate(new Date());
        }
    }

    async _resetWindow(operationType) {
        const counterRef = doc(this.db, 'users', this.userId, 'counters', `rateLimit_${operationType}`);

        try {
            const timestamp = await this._getServerTimestamp();
            const data = {
                count: 1,
                windowStart: timestamp
            };

            await setDoc(counterRef, data);

            this.localCounters[operationType] = {
                count: 1,
                windowStart: this._getMillis(timestamp)
            };
        } catch (error) {
            console.error('Error resetting window:', error);
            const now = Date.now();
            this.localCounters[operationType] = {
                count: 1,
                windowStart: now
            };
        }
    }

    async _initializeCounter(operationType) {
        const counterRef = doc(this.db, 'users', this.userId, 'counters', `rateLimit_${operationType}`);

        try {
            const counterDoc = await getDoc(counterRef);

            if (!counterDoc.exists()) {
                const timestamp = await this._getServerTimestamp();
                const data = {
                    count: 0,
                    windowStart: timestamp
                };

                await setDoc(counterRef, data);

                this.localCounters[operationType] = {
                    count: 0,
                    windowStart: this._getMillis(timestamp)
                };
            } else {
                const serverData = counterDoc.data();
                this.localCounters[operationType] = {
                    count: serverData.count || 0,
                    windowStart: this._getMillis(serverData.windowStart)
                };
            }
        } catch (error) {
            console.error('Error initializing counter:', error);
            const now = Date.now();
            this.localCounters[operationType] = {
                count: 0,
                windowStart: now
            };
        }
    }

    async _syncWithFirestore(operationType) {
        const counterRef = doc(this.db, 'users', this.userId, 'counters', `rateLimit_${operationType}`);
        const counter = this.localCounters[operationType];

        try {
            const counterDoc = await getDoc(counterRef);
            if (!counterDoc.exists()) {
                return this._initializeCounter(operationType);
            }

            const serverData = counterDoc.data();
            if (!(serverData.windowStart instanceof Timestamp)) {
                // If windowStart isn't a valid Timestamp, reinitialize the counter
                return this._initializeCounter(operationType);
            }

            // Only update if counts differ significantly
            if (Math.abs(counter.count - (serverData.count || 0)) > 5) {
                await setDoc(counterRef, {
                    count: Math.max(counter.count, serverData.count || 0),
                    windowStart: serverData.windowStart // Keep the existing Timestamp
                });
            }
        } catch (error) {
            console.error('Error syncing with Firestore:', error);
            // Continue with local counting on sync failure
        }
    }

    async checkLimit(operationType) {
        if (!this.rateLimits[operationType]) {
            throw new Error(`Unknown operation type: ${operationType}`);
        }

        // Initialize counter if needed
        if (!this.localCounters[operationType]) {
            await this._initializeCounter(operationType);
        }

        const counter = this.localCounters[operationType];
        const limit = this.rateLimits[operationType];
        const now = Date.now();

        // Check if window has expired
        if (now - counter.windowStart > this.windowSize) {
            await this._resetWindow(operationType);
            return true;
        }

        // Strictly check against limit before incrementing
        if (counter.count >= limit) {
            throw new Error(`Rate limit exceeded for ${operationType} operations`);
        }

        // Increment counter
        counter.count++;
        console.log(operationType, counter)

        // Sync with server at threshold or at limit
        if (counter.count >= this.syncThreshold || counter.count === limit) {
            await this._syncWithFirestore(operationType);
        }

        return true;
    }

    getOperationCount(operationType) {
        return this.localCounters[operationType]?.count || 0;
    }

    getRemainingOperations(operationType) {
        if (!this.rateLimits[operationType]) return 0;
        const counter = this.localCounters[operationType];
        if (!counter) return this.rateLimits[operationType];
        return Math.max(0, this.rateLimits[operationType] - counter.count);
    }
}